import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { HugeiconsIcon } from '@hugeicons/react';
import { Search01Icon, ArrowRight01Icon, Clock01Icon, SparklesIcon } from '@hugeicons/core-free-icons';
import { CategoryIcon } from '@/components/kb/CategoryIcon';
import { motion, useReducedMotion } from 'framer-motion';
import { UcChristusLogo } from '@/components/brand/InstitutionalBrand';

const DIFFICULTY_COLOR = {
  Principiante: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Intermedio: 'bg-amber-100 text-amber-700 border-amber-200',
  Avanzado: 'bg-rose-100 text-rose-700 border-rose-200',
};

// Artículos cuyo contenido se sirve como página frontend dedicada (contenido
// enriquecido/interactivo) en lugar del artículo genérico de la BD.
const ARTICLE_ROUTE_OVERRIDES = {
  'politica-ia': '/kb/recurso/politica-ia',
};

const articleHref = (slug) => ARTICLE_ROUTE_OVERRIDES[slug] || `/kb/article/${slug}`;

export default function KnowledgeHub() {
  const [categories, setCategories] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();

  useEffect(() => {
    api.get('/kb/categories').then(({ data }) => setCategories(data));
    api.get('/kb/featured').then(({ data }) => setFeatured(data));
  }, []);

  const handleSearch = () => {
    if (search.trim()) navigate(`/kb/buscar?q=${encodeURIComponent(search)}`);
  };

  if (!categories) {
    return (
      <div className="space-y-16">
        <Skeleton className="h-[400px] w-full" />
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const Anim = shouldReduce ? 'div' : motion.div;

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-hero" aria-labelledby="kb-hero-title">
        <div className="absolute inset-0 opacity-60" aria-hidden="true">
          <svg viewBox="0 0 1440 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="w1" x1="0%" y1="0%" x2="100%" y2="50%">
                <stop offset="0%" stopColor="#671E75" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#307FE2" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="w2" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#307FE2" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#671E75" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            <path d="M0,300 Q360,200 720,300 T1440,250" fill="none" stroke="url(#w1)" strokeWidth="2.5" opacity="0.7">
              {!shouldReduce && (
                <animate
                  attributeName="d"
                  values="M0,300 Q360,200 720,300 T1440,250;M0,280 Q360,350 720,260 T1440,300;M0,300 Q360,200 720,300 T1440,250"
                  dur="10s"
                  repeatCount="indefinite"
                />
              )}
            </path>
            <path d="M0,350 Q360,280 720,350 T1440,320" fill="none" stroke="url(#w1)" strokeWidth="1.5" opacity="0.5">
              {!shouldReduce && (
                <animate
                  attributeName="d"
                  values="M0,350 Q360,280 720,350 T1440,320;M0,330 Q360,400 720,310 T1440,360;M0,350 Q360,280 720,350 T1440,320"
                  dur="13s"
                  repeatCount="indefinite"
                />
              )}
            </path>
            <path d="M0,400 Q480,320 960,400 T1440,370" fill="none" stroke="url(#w2)" strokeWidth="1.5" opacity="0.4">
              {!shouldReduce && (
                <animate
                  attributeName="d"
                  values="M0,400 Q480,320 960,400 T1440,370;M0,380 Q480,440 960,360 T1440,410;M0,400 Q480,320 960,400 T1440,370"
                  dur="16s"
                  repeatCount="indefinite"
                />
              )}
            </path>
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
          <Anim
            {...(!shouldReduce
              ? { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } }
              : {})}>
            <div className="flex items-center gap-2 mb-6">
              <UcChristusLogo contrast="light" className="hidden h-5 opacity-90 sm:block" priority />
              <span className="hidden h-5 w-px bg-white/20 sm:block" aria-hidden="true" />
              <span className="text-sm font-medium text-purple-100">VIA — Valor de la IA</span>
            </div>
            <h1
              id="kb-hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] max-w-3xl">
              Aprende, experimenta y crea con{' '}
              <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
                inteligencia artificial
              </span>
            </h1>
            <p className="text-lg text-white/50 mt-6 max-w-2xl leading-relaxed">
              Guías, prompts, buenas prácticas y casos de éxito para adoptar IA de forma segura y productiva.
            </p>
          </Anim>
          <Anim
            {...(!shouldReduce
              ? {
                  initial: { opacity: 0, y: 20 },
                  animate: { opacity: 1, y: 0 },
                  transition: { duration: 0.6, delay: 0.2 },
                }
              : {})}
            className="mt-10 max-w-xl">
            <div className="relative">
              <HugeiconsIcon
                icon={Search01Icon}
                size={20}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40"
                aria-hidden="true"
              />
              <Input
                placeholder="Buscar artículos, prompts, guías..."
                className="pl-13 h-14 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl text-base backdrop-blur-sm focus:bg-white/10 focus:border-white/20 transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                aria-label="Buscar en Knowledge Hub"
              />
            </div>
          </Anim>
        </div>
      </section>

      {/* ── STATS + STARBURST ── */}
      <section className="relative overflow-hidden bg-background">
        <div className="max-w-7xl mx-auto px-6 pt-16">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-foreground text-center mb-12">
            El ecosistema que impulsa la{' '}
            <span className="bg-gradient-to-r from-primary to-[hsl(214,74%,54%)] bg-clip-text text-transparent">
              adopción responsable
            </span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border border border-border rounded-xl overflow-hidden">
            {[
              { value: `${categories.reduce((s, c) => s + c.article_count, 0)}`, label: 'artículos y guías' },
              { value: '4', label: 'ejes de valor VIA' },
              { value: `${categories.length}`, label: 'categorías' },
              { value: '100%', label: 'contenido revisado' },
            ].map((stat) => (
              <div key={stat.label} className="p-6 text-center">
                <p className="font-display text-3xl lg:text-4xl font-extrabold text-foreground tabular">{stat.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-foreground mb-3">
          Explorar por categoría
        </h2>
        <p className="text-base text-muted-foreground max-w-xl mb-10">
          Guías, prompts, videos y políticas curadas por el equipo de gobernanza.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, idx) => (
            <Anim
              key={cat.id}
              {...(!shouldReduce
                ? {
                    initial: { opacity: 0, y: 16 },
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true },
                    transition: { duration: 0.4, delay: idx * 0.06 },
                  }
                : {})}>
              <Link to={`/kb/${cat.slug}`} className="block group h-full">
                <div className="rounded-2xl border border-border/50 bg-card overflow-visible transition-all duration-300 hover:shadow-float hover:-translate-y-1 hover:border-primary/30 h-full flex flex-col">
                  <div className="px-5 pt-5">
                    <div className="h-12 w-12 rounded-full bg-primary shadow-lg shadow-primary/25 flex items-center justify-center">
                      <CategoryIcon slug={cat.slug} className="h-5 w-5 text-primary-foreground" />
                    </div>
                  </div>

                  <div className="flex-1 px-5 pt-3 pb-5 flex flex-col">
                    <h3 className="font-display text-[15px] font-bold text-foreground leading-snug group-hover:text-primary-glow transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed line-clamp-2 flex-1">
                      {cat.description}
                    </p>
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-glow mt-4 group-hover:gap-2.5 transition-all duration-200">
                      Explorar{' '}
                      <HugeiconsIcon
                        icon={ArrowRight01Icon}
                        size={14}
                        className="group-hover:translate-x-0.5 transition-transform"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </div>
              </Link>
            </Anim>
          ))}
        </div>
      </section>

      {/* ── FEATURED — Stripe-style compact cards ── */}
      {featured.length > 0 && (
        <section className="bg-muted/30 border-t border-border/40">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex items-center gap-2 mb-8">
              <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <HugeiconsIcon icon={SparklesIcon} size={14} className="text-primary-glow" aria-hidden="true" />
              </div>
              <h2 className="font-display text-2xl font-extrabold text-foreground">Contenido destacado</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.slice(0, 6).map((art, i) => (
                <Anim
                  key={art.id}
                  className="h-full"
                  {...(!shouldReduce
                    ? {
                        initial: { opacity: 0, y: 12 },
                        whileInView: { opacity: 1, y: 0 },
                        viewport: { once: true },
                        transition: { duration: 0.3, delay: i * 0.05 },
                      }
                    : {})}>
                  <Link to={articleHref(art.slug)} className="block group h-full">
                    <div className="rounded-2xl border border-border bg-card p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-float hover:border-primary/25 h-full flex flex-col">
                      <h3 className="font-display text-sm font-bold text-foreground leading-snug group-hover:text-primary-glow transition-colors">
                        {art.title}
                      </h3>
                      <p className="text-[13px] text-muted-foreground mt-2 leading-relaxed line-clamp-2 flex-1">
                        {art.summary}
                      </p>
                      <div className="flex items-center gap-2.5 mt-3 pt-3 border-t border-border/50">
                        {art.difficulty && (
                          <span className="flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border border-border bg-muted/50">
                            <span
                              className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${art.difficulty === 'Principiante' ? 'bg-emerald-500' : art.difficulty === 'Intermedio' ? 'bg-amber-500' : 'bg-rose-500'}`}
                            />
                            {art.difficulty}
                          </span>
                        )}
                        {art.estimated_minutes && (
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <HugeiconsIcon icon={Clock01Icon} size={12} aria-hidden="true" /> {art.estimated_minutes}{' '}
                            min
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </Anim>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
